'use client'

import { debounce } from 'lodash-es'
import { CSSProperties, FC, MouseEventHandler, ReactElement, useEffect, useRef, useState } from 'react'
import { tss } from 'tss-react/mui'
import { useIntersectionObserver } from 'usehooks-ts'
import { ChevronLeftIcon, ChevronRightIcon } from '../Icon/catalog'
import useIsomorphicLayoutEffect from '../useLayoutEffect'
import useWindowSize from '../useWindowSize'
import { MAX_SCROLLBAR_SIZE, ScrollerProps } from './common'

const { useLayoutEffect } = useIsomorphicLayoutEffect

const useStyles = tss
    .withName('Scroller')
    .withNestedSelectors<'scrollButtonLeft' | 'scrollButtonRight' | 'isRightIntersecting' | 'isLeftIntersecting'>()
    .create(({ theme, classes }) => ({
        scrollButton: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 32,
            color: theme.palette.action.active,
            backgroundColor: theme.palette.common.white,

            [`&.${classes.scrollButtonLeft}`]: {
                left: -8,
                display: 'static',
                boxShadow: `6px 0 6px -6px rgba(0, 0, 0, 0.35)`,

                [`&.${classes.isLeftIntersecting}`]: {
                    display: 'none',
                },
            },
            [`&.${classes.scrollButtonRight}`]: {
                right: -8,
                display: 'static',
                boxShadow: `-6px 0 6px -6px rgba(0, 0, 0, 0.35)`,

                [`&.${classes.isRightIntersecting}`]: {
                    display: 'none',
                },
            },
            '&:hover': {
                color: theme.palette.primary.main,
                cursor: 'pointer',
            },
        },
        scrollerRoot: {
            position: 'relative',
        },
        childrenOuterWrapper: {
            overflowY: 'hidden',
        },
        childrenInnerWrapper: {
            display: 'flex',
            alignItems: 'flex-start',
            flexwrap: 'nowrap',
            overflowX: 'auto',
        },
        scrollButtonLeft: {},
        scrollButtonRight: {},
        isRightIntersecting: {},
        isLeftIntersecting: {},
    }))

const ScrollButton: FC<{
    direction: 'left' | 'right'
    onClick: MouseEventHandler<HTMLDivElement>
    className?: string
    style?: CSSProperties
}> = ({ direction, onClick, className, style }) => {
    const { classes, cx } = useStyles()

    return (
        <div
            role="button"
            onClick={onClick}
            aria-label={`scroll ${direction}`}
            className={cx(
                classes.scrollButton,
                direction === 'left' && classes.scrollButtonLeft,
                direction === 'right' && classes.scrollButtonRight,
                className
            )}
            style={style}
        >
            {direction === 'left' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </div>
    )
}

export default function ScrollerWithButtons({ children, className }: ScrollerProps): ReactElement {
    const { classes, cx } = useStyles()
    const [contentHeight, setContentHeight] = useState(0)
    const [scrollPos, setScrollPos] = useState(0)
    const [scrollDelta, setScrollDelta] = useState(0)
    const heightStyle = { height: contentHeight }
    const { width: windowWidth } = useWindowSize()
    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    // Add a tiny root margin to make sure the markers don't intersect the box at the end scroll positions.
    const { isIntersecting: isLeftIntersecting, ref: markerLeft } = useIntersectionObserver({
        root: containerRef.current,
        rootMargin: '2px',
    })
    const { isIntersecting: isRightIntersecting, ref: markerRight } = useIntersectionObserver({
        root: containerRef.current,
        rootMargin: '2px',
    })

    useEffect(() => {
        const containerNode = containerRef.current

        if (!containerNode) {
            return
        }

        const handleScroll = debounce(() => setScrollPos(containerNode.scrollLeft), 300)

        containerNode.addEventListener('scroll', handleScroll, {
            // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#improving_scrolling_performance_with_passive_listeners
            passive: true,
        })

        return () => containerNode.removeEventListener('scroll', handleScroll)
    }, []) // Pass [] to ensure the function doesn't run on each render

    useLayoutEffect(() => {
        if (!contentRef.current || !containerRef.current) {
            return
        }

        setContentHeight(contentRef.current.offsetHeight)
        setScrollDelta(containerRef.current.offsetWidth)
        containerRef.current.scrollLeft = scrollPos
    }, [windowWidth, scrollPos])

    return (
        <div className={cx(classes.scrollerRoot, className)}>
            <div className={classes.childrenOuterWrapper} style={heightStyle}>
                <div
                    className={classes.childrenInnerWrapper}
                    style={{ height: contentHeight + MAX_SCROLLBAR_SIZE }}
                    ref={containerRef}
                >
                    <div ref={markerLeft} />
                    <div ref={contentRef}>{children}</div>
                    <div ref={markerRight} />
                </div>
            </div>
            <ScrollButton
                direction="left"
                onClick={() => setScrollPos((pos) => pos - scrollDelta)}
                style={heightStyle}
                className={cx(isLeftIntersecting && classes.isLeftIntersecting)}
            />
            <ScrollButton
                direction="right"
                onClick={() => setScrollPos((pos) => pos + scrollDelta)}
                style={heightStyle}
                className={cx(isRightIntersecting && classes.isRightIntersecting)}
            />
        </div>
    )
}
